import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { Popup } from 'components';
import { callApi } from 'utils';

const emptyResult = Object.freeze({jobId: null, data: null, error: ''});

function formatNumber(value) {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  return Number.isFinite(value) ? `${formatNumber(value)}%` : '—';
}

function formatMetricValue(value, unit) {
  return unit === 'number' ? formatNumber(value) : formatPercent(value);
}

function formatRange(range, unit) {
  const min = Number.isFinite(range?.min) ? range.min : null;
  const max = Number.isFinite(range?.max) ? range.max : null;
  if (min !== null && max !== null) {
    return `${formatMetricValue(min, unit)}–${formatMetricValue(max, unit)}`;
  }
  if (min !== null) {
    return `≥${formatMetricValue(min, unit)}`;
  }
  if (max !== null) {
    return `≤${formatMetricValue(max, unit)}`;
  }
  return '—';
}

const DetailRow = ({label, children}) => (
  <React.Fragment>
    <Typography component="dt" color="text.secondary" sx={{m: 0}}>{label}</Typography>
    <Typography component="dd" sx={{m: 0, minWidth: 0, overflowWrap: 'anywhere'}}>{children}</Typography>
  </React.Fragment>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const MonitorCard = ({monitor}) => {
  const metric = monitor.metricLabel || monitor.metric;
  const unit = monitor.unit === 'number' ? 'number' : 'percent';
  const formula = unit === 'percent'
    ? [monitor.numeratorLabel, monitor.denominatorLabel].filter(Boolean).join(' / ')
    : '';
  const sampleMinimum = Number.isFinite(monitor.sampleMinimum) ? monitor.sampleMinimum : null;

  return (
    <Card variant="outlined" sx={{p: 2}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1.5}}>{monitor.title}</Typography>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: {xs: '92px minmax(0, 1fr)', sm: '112px minmax(0, 1fr)'},
          rowGap: 1,
          columnGap: 2,
        }}
      >
        <DetailRow label="监测指标">{formula ? `${metric}（${formula}）` : metric}</DetailRow>
        <DetailRow label="业务目标">{monitor.businessTarget || '—'}</DetailRow>
        <DetailRow label="正常区间">{formatRange(monitor.expected, unit)}</DetailRow>
        <DetailRow label="当前值">{formatMetricValue(monitor.actual, unit)}</DetailRow>
        {unit === 'percent' && (
        <React.Fragment>
          <DetailRow label={monitor.numeratorLabel || '分子'}>{formatNumber(monitor.numerator)}</DetailRow>
          <DetailRow label={monitor.denominatorLabel || '分母'}>{formatNumber(monitor.denominator)}</DetailRow>
        </React.Fragment>
        )}
        {sampleMinimum !== null && sampleMinimum > 0 && (
        <DetailRow label="样本门槛">
          {`${monitor.sampleMetric || 'Landing'} ≥ ${formatNumber(sampleMinimum)}（当前 ${formatNumber(monitor.sampleValue)}）`}
        </DetailRow>
        )}
      </Box>
    </Card>
  );
};

MonitorCard.propTypes = {
  monitor: PropTypes.shape({
    rule: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    metric: PropTypes.string,
    metricLabel: PropTypes.string,
    unit: PropTypes.oneOf(['number', 'percent']),
    businessTarget: PropTypes.string,
    expected: PropTypes.shape({min: PropTypes.number, max: PropTypes.number}),
    actual: PropTypes.number,
    numerator: PropTypes.number,
    numeratorLabel: PropTypes.string,
    denominator: PropTypes.number,
    denominatorLabel: PropTypes.string,
    sampleMetric: PropTypes.string,
    sampleValue: PropTypes.number,
    sampleMinimum: PropTypes.number,
  }).isRequired,
};

const CustomMonitorDialog = ({job, onClose}) => {
  const [result, setResult] = useState(emptyResult);

  useEffect(() => {
    if (!job) {
      setResult(emptyResult);
      return undefined;
    }

    let active = true;
    setResult({jobId: job.id, data: null, error: ''});
    callApi(`/jobs/${job.id}/custom-monitors`)
      .then(data => active && setResult({jobId: job.id, data, error: ''}))
      .catch(error => {
        console.error(error);
        active && setResult({jobId: job.id, data: null, error: error.message || String(error)});
      });
    return () => {
      active = false;
    };
  }, [job]);

  if (!job) {
    return null;
  }

  const currentResult = result.jobId === job.id ? result : emptyResult;
  const loading = !currentResult.data && !currentResult.error;
  const monitors = currentResult.data?.monitors || [];
  const capturedAt = currentResult.data?.capturedAt
    ? dayjs(currentResult.data.capturedAt).tz(currentResult.data.timeZone).format('YYYY-MM-DD HH:mm:ss')
    : '—';

  return (
    <Popup
      open
      onClose={onClose}
      title={`自定义监控 - ${job.id}: ${job.name || ''}`}
      styles={{width: '720px'}}
    >
      {loading && (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 3}}>
        <CircularProgress size={24} />
        <Typography>正在读取监控设置和当前数据</Typography>
      </Box>
      )}
      {currentResult.error && (
      <Typography color="error" sx={{py: 2}}>{currentResult.error}</Typography>
      )}
      {currentResult.data && monitors.length === 0 && (
      <Typography sx={{py: 3}}>该任务未配置自定义监控</Typography>
      )}
      {monitors.length > 0 && (
      <Stack spacing={2} sx={{pt: 2}}>
        {monitors.map(monitor => (
          <MonitorCard key={`${monitor.rule}:${monitor.metric}`} monitor={monitor} />
        ))}
        <Divider />
        <Box sx={{display: 'grid', gridTemplateColumns: '88px minmax(0, 1fr)', rowGap: 0.5, columnGap: 1}}>
          <Typography color="text.secondary">业务日期</Typography>
          <Typography>{currentResult.data.businessDate || '—'}</Typography>
          <Typography color="text.secondary">环境时区</Typography>
          <Typography>{currentResult.data.timeZone || '—'}</Typography>
          <Typography color="text.secondary">数据时间</Typography>
          <Typography>{capturedAt}</Typography>
        </Box>
      </Stack>
      )}
    </Popup>
  );
};

CustomMonitorDialog.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default CustomMonitorDialog;
